import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { subscriptionDataService, subscriptionUtils } from '../services/subscriptionService';
import { isSubscriptionModuleEnabled, isAlwaysAccessibleRoute, isAlwaysAccessibleFeature } from '../config/subscriptionConfig';

/**
 * Hook pour gérer l'accès basé sur l'abonnement
 * - Si le module est désactivé : Accès complet pour tous
 * - Si le module est activé :
 *   - Premium/Offert : Accès complet selon les permissions du rôle
 *   - Gratuit/Expiré/Aucun : Accès limité au dashboard et à la page d'abonnement uniquement
 */
export const useSubscriptionAccess = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState('none'); // 'premium', 'limited', 'none'

  const auth = getAuth();
  const user = auth.currentUser;

  // Si le module d'abonnement est désactivé, donner accès premium à tous
  const moduleEnabled = isSubscriptionModuleEnabled();

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setAccessLevel('none');
      return;
    }

    // Si le module est désactivé, donner accès premium automatiquement
    if (!moduleEnabled) {
      setAccessLevel('premium');
      setLoading(false);
      return;
    }

    loadSubscription();
  }, [user, moduleEnabled]);

  const loadSubscription = async () => {
    try {
      setLoading(true);

      const currentSubscription = await subscriptionDataService.getCurrentSubscription(db, user.uid);
      setSubscription(currentSubscription);

      // Déterminer le niveau d'accès
      if (!currentSubscription) {
        setAccessLevel('limited'); // Pas d'abonnement = accès limité
        return;
      }

      const isActive = subscriptionUtils.isSubscriptionActive(currentSubscription);
      const isFree = subscriptionUtils.isFreeSubscription(currentSubscription);
      const isOffered = subscriptionUtils.isOfferedSubscription(currentSubscription);

      if (!isActive) {
        // Abonnement expiré = accès limité
        setAccessLevel('limited');
      } else if (isFree) {
        // Abonnement gratuit (essai 30 jours) = accès complet pour tester toutes les fonctionnalités
        setAccessLevel('premium');
      } else if (isOffered || currentSubscription.status === 'active') {
        // Abonnement offert ou premium = accès complet
        setAccessLevel('premium');
      } else {
        // Par défaut, accès limité
        setAccessLevel('limited');
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'abonnement:', error);
      setAccessLevel('limited');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si l'utilisateur a accès premium (offert ou payant)
  const hasPremiumAccess = () => {
    return accessLevel === 'premium';
  };

  // Vérifier si l'utilisateur a un accès limité (gratuit, expiré, aucun)
  const hasLimitedAccess = () => {
    return accessLevel === 'limited';
  };

  // Vérifier si une route spécifique est accessible
  const canAccessRoute = (route) => {
    // Si le module est désactivé, toutes les routes sont accessibles
    if (!moduleEnabled) {
      return true;
    }

    // Si la route est dans la liste des routes toujours accessibles
    if (isAlwaysAccessibleRoute(route)) {
      return true;
    }

    // Pour toutes les autres routes, il faut un accès premium
    return hasPremiumAccess();
  };

  // Vérifier si une fonctionnalité est accessible
  const canAccessFeature = (feature) => {
    // Si le module est désactivé, toutes les fonctionnalités sont accessibles
    if (!moduleEnabled) {
      return true;
    }

    // Si la fonctionnalité est dans la liste des fonctionnalités toujours accessibles
    if (isAlwaysAccessibleFeature(feature)) {
      return true;
    }

    // Pour toutes les autres fonctionnalités, il faut un accès premium
    return hasPremiumAccess();
  };

  return {
    subscription,
    loading,
    accessLevel,
    hasPremiumAccess: hasPremiumAccess(),
    hasLimitedAccess: hasLimitedAccess(),
    canAccessRoute,
    canAccessFeature,
    reloadSubscription: loadSubscription
  };
};
